import { Box, IconButton, Paper, Slider, Stack, Tooltip } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import StartIcon from "@mui/icons-material/Start";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import useEventListener from "../../../Utils/Hooks/useEventListener";
import SwitchPlayerMeter from "./switch-player-meter";
import { useTranslation } from "react-i18next";

export type SwitchPlayerProps = {
  setSwitchData: React.Dispatch<any>;
  path: string;
};

const SwitchPlayer: React.FC<SwitchPlayerProps> = (props) => {
  const { t } = useTranslation();
  const { path } = props;

  // State
  const [websocket, setWebSocket] = useState<WebSocket | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const [silence, setSilence] = useState<any>(new Float32Array((8200 - 8) / 2));
  const [audioBufferSize, setAudioBufferSize] = useState(4096);
  const [audioChannelCounts, setAudioChannelCounts] = useState<any[]>([0]);
  const [audioPlaybackChannelCount, setAudioPlaybackChannelCount] = useState(2);
  const [audioTrackCount, setAudioTrackCount] = useState(0);
  const [subtitleTrackCount, setSubtitleTrackCount] = useState(0);
  const [highWaterMarks, setHighWaterMarks] = useState<any[]>([]);
  const [silenceDB, setSilenceDB] = useState<number>(-72);
  const [frameIndex, setFrameIndex] = useState(0);
  const [frameCount, setFrameCount] = useState(0);
  const [askForProperties, setAskForProperties] = useState(false);
  const [audioData, setAudioData] = useState<any>();
  const [currentTimecode, setCurrentTimecode] = useState("00:00.00");
  const [frameRate, setFrameRate] = useState("25 fps");

  // Refs
  const videoImage = useRef<any>(undefined);
  const audioQueueRef = useRef<any[]>([]);
  const audioScriptProcessorNode = useRef<any>(undefined);
  const audioContext = useRef<AudioContext | null>(null);
  const audioStarted = useRef<boolean>(false);
  const audioPlaying = useRef<boolean>(false);
  const bypassNextFrame = useRef<boolean>(false);
  const videoReceivedTimeInMilliseconds = useRef<number>(0);

  // socket/switch pipline functions

  function connect() {
    if ("WebSocket" in window) {
      let newwebsocket = new WebSocket("ws://localhost:1234/");
      newwebsocket.binaryType = "arraybuffer";
      videoImage.current = document.getElementById("video");

      newwebsocket.onopen = function () {
        setAskForProperties(true);
      };
      newwebsocket.onmessage = handleSocketMsg;

      newwebsocket.onerror = function (error) {
        console.log("newwebsocket Error: ", error);
      };

      newwebsocket.onclose = function () {
        setTimeout(() => {
          connect();
        }, 500);
      };

      setWebSocket(newwebsocket);
    } else {
      alert(t("alert:error.websocketsNotSupported"));
    }
  }

  function startServer() {
    var uri = "switch://server?port=1234&file=" + encodeURI(path);
    window.open(uri, "_blank", "noreferrer");
    window.focus();
  }

  const handleSocketMsg = (evt) => {
    // Binary data.
    if (evt.data instanceof ArrayBuffer) {
      var dv = new DataView(evt.data, 0);
      var dataType = dv.getInt8(0);

      if (dataType == 1) {
        var jpeg = new Uint8Array(evt.data, 8);
        var blob = new Blob([jpeg], { type: "image/jpeg" });
        var url = URL.createObjectURL(blob);
        videoImage.current.onload = function () {
          URL.revokeObjectURL(url);
        };
        videoImage.current.src = url;
      } else if (dataType == 2) {
        const floatArray = new Float32Array(evt.data);
        audioQueueRef.current = [...audioQueueRef.current, floatArray];

        if (audioContext.current == null) {
          setAudioBufferSize((evt.data.byteLength - 8) / audioPlaybackChannelCount);
          setSilence(new Float32Array((evt.data.byteLength - 8) / audioPlaybackChannelCount));
        }

        if (audioStarted.current == false && audioQueueRef.current.length > 2) {
          playAudio();
        }
      }
    }

    // Textual data.
    else {
      var data = JSON.parse(evt.data);
      switch (data["kind"]) {
        case "image":
          receivedImage(data);
          break;
        case "video":
          receivedVideo(data);
          break;
        case "container properties":
          receivedContainerProperties(data);
          break;
        case "video properties":
          receivedVideoProperties(data);
          break;
        case "audio properties":
          receivedAudioProperties(data);
          break;
        case "subtitle properties":
          receivedSubtitleProperties(data);
          break;
        case "captions settings":
          break;
        case "license level":
          receivedLicenseLevel(data);
          break;
        case "audio meters":
          receivedAudioMeters(data);
          break;
      }
    }
  };

  function receivedImage(image) {
    videoImage.current.src = "data:image/jpeg;base64," + image["image"];
    setFrameIndex(image["index"]);
  }

  function receivedVideo(video) {
    if (video["index"] >= 0) setFrameIndex(video["index"]);
    if (video["timecodes"]) setCurrentTimecode(video["timecodes"][Object.keys(video["timecodes"])[0]]);
    if (bypassNextFrame.current) {
      bypassNextFrame.current = false;
    } else {
      var now = new Date();
      videoReceivedTimeInMilliseconds.current = now.getTime();
    }
  }

  function receivedContainerProperties(properties) {
    props.setSwitchData((x) => ({ ...x, containerProps: properties }));
    setAudioTrackCount(properties["audio track count"]);
    setSubtitleTrackCount(properties["subtitle track count"]);
    setAudioChannelCounts(Array.apply(null, Array(properties["audio track count"])).map(function () {}));
  }

  function receivedVideoProperties(properties) {
    props.setSwitchData((x) => ({ ...x, videoProps: properties }));
    if (properties["frame count"]) setFrameCount(properties["frame count"]);
    if (properties["Frame Rate And Field Order"]) setFrameRate(properties["Frame Rate And Field Order"].split(",")[0]);
  }

  function receivedAudioProperties(properties) {
    props.setSwitchData((x) => ({
      ...x,
      audioProps: x.audioProps
        ? [...x.audioProps.filter((a) => a.index !== properties.index), properties]
        : [properties],
    }));
    setAudioChannelCounts((x) => ({ ...x, [properties["index"]]: properties["channel count"] }));
  }

  function receivedSubtitleProperties(properties) {
    props.setSwitchData((x) => ({ ...x, subtitleProps: properties }));
  }

  function receivedLicenseLevel(data) {
    alert("License: " + data["level name"] + " (" + data["level number"] + ")");
  }

  function receivedAudioMeters(data) {
    setAudioData(data);
  }

  function send(command) {
    if (command.indexOf("captions ") !== 0 && command.indexOf("inspect ") !== 0) {
      audioQueueRef.current = [];
    }
    websocket?.send(command);
  }

  function playAudio() {
    if (audioStarted.current == false) {
      audioStarted.current = true;
      audioScriptProcessorNode.current.connect(audioContext.current!.destination);
    }

    audioPlaying.current = true;
  }

  function pauseAudio() {
    audioPlaying.current = false;
    audioScriptProcessorNode.current.connect(audioContext.current!.destination);
  }

  // Ui events handler

  function pause() {
    send("pause");
    pauseAudio();
  }

  function play() {
    send("play");
    audioPlaying.current = true;
  }

  const handleShortcut = (e: KeyboardEvent) => {
    const activeElement = document.activeElement;
    if (activeElement && activeElement.tagName.toLowerCase() === "input") {
      return;
    }
    e.stopPropagation();
    switch (e.key) {
      case "k":
      case " ":
        isPlaying ? pause() : play();
        break;
      case "l":
        send("l");
        break;
      case "j":
        send("j");
        break;
      case ";":
        send("step backward");
        break;
      case ":":
        send("step forward");
        break;
      case ",":
        send("skip backward 10");
        break;
      case "!":
        send("skip forward 10");
        break;
      default:
        break;
    }
  };
  useEventListener("keydown", handleShortcut);

  const handleSliderChange = (e) => {
    bypassNextFrame.current = true;
    send("jump to frame " + e.target.value);
  };

  function videoIsPaused() {
    var now = new Date();
    if (bypassNextFrame.current) return true;
    return videoReceivedTimeInMilliseconds.current < now.getTime() - 250;
  }

  // Effects chain

  useEffect(() => {
    const timeIntervalIsPlayingCheck = setInterval(() => {
      if (videoIsPaused()) setIsPlaying(false);
      else setIsPlaying(true);
    }, 250);

    const context = new window.AudioContext();
    audioScriptProcessorNode.current = context.createScriptProcessor(audioBufferSize, 2, 2);
    audioScriptProcessorNode.current.onaudioprocess = function (audioProcessingEvent) {
      if (audioPlaying.current == false || audioQueueRef.current.length < 1) {
        audioProcessingEvent.outputBuffer.getChannelData(0).set(silence);
        audioProcessingEvent.outputBuffer.getChannelData(1).set(silence);

        return;
      }
      var chunk = audioQueueRef.current?.shift() || null;
      if (chunk != null) {
        var channel1 = audioProcessingEvent.outputBuffer.getChannelData(0);
        var channel2 = audioProcessingEvent.outputBuffer.getChannelData(1);
        for (var interleavedSampleIndex = 0; interleavedSampleIndex < chunk.length; interleavedSampleIndex++) {
          var nonInterleavedSampleIndex = Math.floor(interleavedSampleIndex / audioPlaybackChannelCount);
          if ((interleavedSampleIndex + 1) % audioPlaybackChannelCount == 0)
            channel2[nonInterleavedSampleIndex] = chunk[interleavedSampleIndex];
          else channel1[nonInterleavedSampleIndex] = chunk[interleavedSampleIndex];
        }
      }
    };
    audioContext.current = context;
    return () => clearInterval(timeIntervalIsPlayingCheck);
  }, []);

  useEffect(() => {
    connect();
  }, [path]);

  useEffect(() => {
    if (websocket) {
      startServer();
    }
  }, [websocket]);

  useEffect(() => {
    if (audioTrackCount && audioTrackCount > 0 && websocket?.readyState === 1)
      for (var i = 0; i < audioTrackCount; i++) {
        websocket.send(`inspect audio ${i}`);
      }
  }, [websocket?.readyState === 1, audioTrackCount]);

  useEffect(() => {
    if (subtitleTrackCount && subtitleTrackCount > 0 && websocket?.readyState === 1)
      for (let subtitleTrackIndex = 0; subtitleTrackIndex < subtitleTrackCount; subtitleTrackIndex++)
        websocket?.send("inspect subtitle " + subtitleTrackIndex);
  }, [websocket?.readyState === 1, subtitleTrackCount]);

  useEffect(() => {
    if (askForProperties && websocket?.readyState === 1) {
      websocket.send("Placeholder message");
      websocket.send("inspect container");
      websocket.send("send meters");
      websocket.send("captions settings");
      websocket.send("image at index 0");
      websocket.send("inspect video");
      setAskForProperties(false);
    }
  }, [askForProperties, websocket?.readyState === 1]);

  // Change video opacity when reach end
  useEffect(() => {
    if (frameIndex >= frameCount - 2) {
      videoImage.current.style = "opacity: 0.3;";
    } else {
      videoImage.current.style = "";
    }
  }, [frameCount, frameIndex]);

  // Memo
  const renderControls = useMemo(
    () => (
      <Stack mt={-1} width={"100%"} justifyContent={"center"} display={"flex"} direction={"row"}>
        <Tooltip title={t("tooltip:title.jumpToStart")}>
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send("jump to start");
            }}
          >
            <StartIcon sx={{ transform: "rotate(180deg)" }} fontSize="large" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.skipBackward", { count: 10 })}>
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send("skip backward 10");
            }}
          >
            <SkipPreviousIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.skipBackward", { count: 1 })}>
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send("skip backward 1");
            }}
          >
            <FastRewindIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        <Tooltip title="-1 Frame">
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send("step backward");
            }}
          >
            <ArrowLeftIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        {isPlaying ? (
          <Tooltip title={t("common:pause")}>
            <IconButton onClick={pause}>
              <PauseCircleOutlineIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t("common:play")}>
            <IconButton onClick={play}>
              <PlayCircleOutlineIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t("tooltip:title.plusFrame", { count: 1 })}>
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send("step forward");
            }}
          >
            <ArrowRightIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.skipForward", { count: 1 })}>
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send("skip forward 1");
            }}
          >
            <FastForwardIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.skipForward", { count: 10 })}>
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send("skip forward 10");
            }}
          >
            <SkipNextIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.jumpToEnd")}>
          <IconButton
            onClick={() => {
              bypassNextFrame.current = true;
              send(`jump to frame ${frameCount - 2}`);
            }}
          >
            <StartIcon fontSize="large" />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
    [isPlaying, websocket]
  );

  return (
    <>
      <div style={{ minHeight: "390px" }}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "column",
            backgroundImage: "none",
            backgroundColor: "background.default",
            justifyContent: "center",
            height: 30,
            textAlign: "right",
            borderRadius: "6px 6px 0px 0px",
          }}
        >
          {currentTimecode + " " + "@" + " " + frameRate}
        </Paper>
        <Box width={"100%"} display={"flex"} alignItems={"center"} flexDirection={"column"}>
          {
            <img
              style={{ minWidth: "672px", minHeight: "378px", width: "100%", height: "auto" }}
              id="video"
              alt="Video will appear here."
            />
          }
          <Slider onChange={handleSliderChange} max={frameCount + 1} value={frameIndex} sx={{ width: "97%" }} />
        </Box>

        {renderControls}
        <SwitchPlayerMeter
          audioChannelCounts={audioChannelCounts}
          audioPlaybackChannelCount={audioPlaybackChannelCount}
          data={audioData}
          highWaterMarks={highWaterMarks}
          setHighWaterMarks={setHighWaterMarks}
          silenceDB={silenceDB}
        />
      </div>
    </>
  );
};

export default SwitchPlayer;
